import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { fetchWithStartupRetry } from "@/utils/retryOnStartup";
import { UserStatusType, StatusContext } from "@velaro/velaro-component-lib";

export default function StatusProvider(props: { children: React.ReactNode }) {
  const [state, setState] = useState<UserStatusType>("Unavailable");
  const api = useApi();

  useEffect(() => {
    async function fetchStatus() {
      try {
        const resp = await fetchWithStartupRetry(() =>
          api.messaging.get("UserStatus")
        );
        if (resp.ok) {
          const data = await resp.json();
          setState(data.status ?? "Unavailable");
        }
      } catch (ex) {
        console.error("StatusProvider: failed to fetch user status", ex);
      }
    }

    fetchStatus();
  }, [api.messaging]);

  // Presence heartbeat — sends every 5 min so the messaging routing engine
  // knows this user is still active even when they're working in admin.
  // Without this, LastHeartbeat goes stale after 15 min and the user is
  // excluded from chat routing despite being marked Available.
  useEffect(() => {
    const sendHeartbeat = () => {
      api.messaging.post("UserStatus/heartbeat", {}).catch(() => {});
    };
    sendHeartbeat(); // immediate on mount
    const id = setInterval(sendHeartbeat, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [api.messaging]);

  function setStatus(status: UserStatusType) {
    setState(status);
    api.messaging.post("UserStatus", { status });
  }

  return (
    <StatusContext.Provider value={{ status: state, setStatus }}>
      {props.children}
    </StatusContext.Provider>
  );
}
